import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Table from "../../components/table";
import { fetchTransactions } from "../../api/endpoint";
import axios from "axios";
import { saveTransactionsData } from "../../store/slice/transactionsSlice";
import Input from "../../components/input";
import Button from "../../components/button";
import Loader from "../../components/loader";
import RefundModal from "./refundModal";
import { convertUTCToLocalString } from "../../utils/format";

const Transactions = () => {
  const dispatch = useDispatch();
  const [subscriptionTransactions, setSubscriptionsTransactions] = useState([]);
  const [payPerUseTransactions, setPayPerUseTransactions] = useState([]);
  const [isDropdownFilterOpen, setIsDropdownFilterOpen] = useState(false);
  const [filters, setFilters] = useState({
    name: "",
    email: "",
    startDate: "",
    endDate: "",
  });
  const [loading, setLoading] = useState(false);
  const { auth } = useSelector((state) => state.login);
  const [activeTab, setActiveTab] = useState("Subscriptions");
  const [selectedTransaction, setSelectedTransaction] = useState({});
  const [isRefundOpen, setIsRefundOpen] = useState(false);

  // pagination
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize] = useState(10);
  const [totalElements, setTotalElements] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const baseHeadings = [
    { name: "Name", fieldName: "name", isSortable: true, sortKey: "none" },
    { name: "Email", fieldName: "email", isSortable: true, sortKey: "none" },
    {
      name: "Amount",
      fieldName: "amount",
      isSortable: true,
      sortKey: "none",
      filter: false,
    },
    {
      name: "Date",
      fieldName: "transactionDate",
      isSortable: true,
      sortKey: "none",
      dateFilter: true,
    },
  ];

  const headings =
    activeTab === "PayPerUse"
      ? [...baseHeadings, { name: "Actions", fieldName: "actions" }]
      : baseHeadings;

  useEffect(() => {
    getTransactions(filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  const getTransactions = async (filters) => {
    try {
      setLoading(true);
      const response = await axios.get(fetchTransactions, {
        headers: {
          Authorization: `Bearer ${auth}`,
        },
        params: {
          name: filters?.name,
          email: filters?.email,
          startDate: filters?.startDate,
          endDate: filters?.endDate,
          page: currentPage,
          size: pageSize,
        },
      });

      const { content, totalElements, totalPages } = response.data;

      let subscriptions = content.filter((txn) =>
        txn.transactionId.startsWith("sub")
      );
      subscriptions = subscriptions?.map((txn) => ({
        ...txn,
        transactionDate: convertUTCToLocalString(txn?.transactionDate),
      }));

      let payPerUse = content.filter((txn) =>
        txn.transactionId.startsWith("pi")
      );
      payPerUse = payPerUse?.map((txn) => ({
        ...txn,
        transactionDate: convertUTCToLocalString(txn?.transactionDate),
      }));

      setSubscriptionsTransactions(subscriptions);
      setPayPerUseTransactions(payPerUse);

      dispatch(saveTransactionsData(content));
      setTotalElements(totalElements);
      setTotalPages(totalPages);
    } catch (error) {
      console.error("Error fetching transactions:", error);
    } finally {
      setLoading(false);
    }
  };

  const filtersDropdown = () => (
    <form>
      <div className="row mb-3">
        <label className="col-sm-4 col-form-label">Name:</label>
        <div className="col-sm-8">
          <Input
            type="text"
            placeholder="Enter User's Name"
            value={filters?.name}
            onChange={(e) => setFilters({ ...filters, name: e.target.value })}
          />
        </div>
      </div>
      <div className="row mb-3">
        <label className="col-sm-4 col-form-label">Email:</label>
        <div className="col-sm-8">
          <Input
            type="text"
            placeholder="Enter User's Email"
            value={filters?.email}
            onChange={(e) => setFilters({ ...filters, email: e.target.value })}
          />
        </div>
      </div>
      <div className="row mb-3">
        <label className="col-sm-4 col-form-label">Range Start Date:</label>
        <div className="col-sm-8">
          <Input
            type="date"
            value={filters?.startDate}
            onChange={(e) =>
              setFilters({ ...filters, startDate: e.target.value })
            }
          />
        </div>
      </div>
      <div className="row mb-3">
        <label className="col-sm-4 col-form-label">Range End Date:</label>
        <div className="col-sm-8">
          <Input
            type="date"
            value={filters?.endDate}
            onChange={(e) =>
              setFilters({ ...filters, endDate: e.target.value })
            }
          />
        </div>
      </div>
      <div className="d-flex gap-1 float-end">
        <Button
          text="Apply Filters"
          callBack={(e) => {
            applyFilters(e);
            setIsDropdownFilterOpen(false);
          }}
        />
        <Button
          text="Clear All"
          callBack={(e) => {
            clearFilters(e);
            setIsDropdownFilterOpen(false);
          }}
        />
      </div>
    </form>
  );

  const applyFilters = (e) => {
    e.preventDefault();
    setCurrentPage(0);
    getTransactions(filters);
  };

  const clearFilters = (e) => {
    e.preventDefault();
    const cleared = { name: "", email: "", startDate: "", endDate: "" };
    setFilters(cleared);
    setCurrentPage(0);
    getTransactions(cleared);
  };

  const getFilteredData = (data) => {
    const allowedFields = headings.map((h) => h.fieldName);

    return data.map((txn) => {
      const filteredTxn = Object.fromEntries(
        Object.entries(txn).filter(([key]) => allowedFields.includes(key))
      );

      if (activeTab === "PayPerUse") {
        filteredTxn.actions = (
          <Button
            text="Refund"
            callBack={() => handleRefund(txn)}
            className="btn-sm"
            disabled={!txn?.isRefundEligible}
          />
        );
      }
      return filteredTxn;
    });
  };

  const activeData =
    activeTab === "Subscriptions"
      ? subscriptionTransactions
      : payPerUseTransactions;

  const handlePageChange = (pageNumber) => {
    if (pageNumber >= 0 && pageNumber < totalPages) {
      setCurrentPage(pageNumber);
    }
  };

  const handleRefund = (txn) => {
    setSelectedTransaction(txn);
    setIsRefundOpen(true);
  };

  return (
    <>
      {loading && <Loader />}
      <div className="px-md-2 px-1 py-md-2 py-1">
        {/* Tabs */}
        <ul className="nav nav-tabs mb-3">
          <li className="nav-item" style={{ cursor: "pointer" }}>
            <a
              className={`nav-link ${
                activeTab === "Subscriptions" ? "active" : ""
              }`}
              onClick={() => {
                setActiveTab("Subscriptions");
                setCurrentPage(0);
              }}
              style={{
                color: activeTab !== "Subscriptions" ? "#2c3e50" : "",
                fontWeight: activeTab === "Subscriptions" ? "600" : "normal",
                fontSize: activeTab === "Subscriptions" ? "large" : "medium",
              }}
            >
              Subscriptions
            </a>
          </li>
          <li className="nav-item" style={{ cursor: "pointer" }}>
            <a
              className={`nav-link ${
                activeTab === "PayPerUse" ? "active" : ""
              }`}
              onClick={() => {
                setActiveTab("PayPerUse");
                setCurrentPage(0);
              }}
              style={{
                color: activeTab !== "PayPerUse" ? "#2c3e50" : "",
                fontWeight: activeTab === "PayPerUse" ? "600" : "normal",
                fontSize: activeTab === "PayPerUse" ? "large" : "medium",
              }}
            >
              Pay Per Use
            </a>
          </li>
        </ul>

        {/* Table */}
        <Table
          headings={headings}
          data={getFilteredData(activeData)}
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
          enablePagination={totalPages > 1}
          isDropdownFilterOpen={isDropdownFilterOpen}
          setIsDropdownFilterOpen={setIsDropdownFilterOpen}
          filtersDropdown={() => filtersDropdown()}
          card={true}
        />
      </div>
      <RefundModal
        isRefundOpen={isRefundOpen}
        closeRefundModal={() => setIsRefundOpen(false)}
        selectedTransaction={selectedTransaction}
        fetchTransactions={getTransactions}
      />
    </>
  );
};

export default Transactions;
